import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import usersApi from "@/api/usersApi";
import UserProfileResponse from "@/model/UserProfileResponse";
import "@/components/UserIdsSelect/UserIdsSelect.scss";

const USERS_PAGE_SIZE = 100;

interface UserIdsSelectProps {
  value: string[];
  onChange: (userIds: string[]) => void;
}

function UserIdsSelect({ value, onChange }: UserIdsSelectProps) {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [hosts, setHosts] = useState<UserProfileResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    usersApi
      .listUsers(session.accessToken, 0, USERS_PAGE_SIZE)
      .then((result) => {
        if (!cancelled) {
          setHosts(
            (result.content ?? []).filter((user) => user.role === "HOST")
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedIds = Array.from(event.target.selectedOptions).map(
      (option) => option.value
    );
    onChange(selectedIds);
  };

  return (
    <div className="user-ids-select">
      <label htmlFor="userIds">{t("companies.userIdsSelect.label")}</label>
      <select
        id="userIds"
        multiple
        value={value}
        onChange={handleChange}
        disabled={isLoading || error}
      >
        {hosts
          .filter(
            (host): host is UserProfileResponse & { id: string } =>
              Boolean(host.id)
          )
          .map((host) => (
            <option key={host.id} value={host.id}>
              {[host.firstName, host.lastName].filter(Boolean).join(" ") ||
                host.email ||
                host.id}
              {host.email ? ` (${host.email})` : ""}
            </option>
          ))}
      </select>
      {isLoading && (
        <p className="user-ids-select__help">
          {t("companies.userIdsSelect.loading")}
        </p>
      )}
      {error && (
        <p className="user-ids-select__error">
          {t("companies.userIdsSelect.loadError")}
        </p>
      )}
      {!isLoading && !error && hosts.length === 0 && (
        <p className="user-ids-select__help">
          {t("companies.userIdsSelect.empty")}
        </p>
      )}
      {!isLoading && !error && hosts.length > 0 && (
        <p className="user-ids-select__help">
          {t("companies.userIdsSelect.help")}
        </p>
      )}
    </div>
  );
}

export default UserIdsSelect;
export type { UserIdsSelectProps };
